import React, { useEffect, useState, useRef, useCallback } from 'react';
import { MapContainer, TileLayer, Marker, Popup, Polyline } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';

// Fix Leaflet default icons
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png',
});

// Custom Icons
const pickupIcon = new L.Icon({
  iconUrl: 'https://cdn-icons-png.flaticon.com/512/684/684908.png',
  iconSize: [32, 32],
  iconAnchor: [16, 32],
  popupAnchor: [0, -32],
});

const dropoffIcon = new L.Icon({
  iconUrl: 'https://cdn-icons-png.flaticon.com/512/684/684840.png',
  iconSize: [32, 32],
  iconAnchor: [16, 32],
  popupAnchor: [0, -32],
});

const driverIcon = new L.Icon({
  iconUrl: 'https://cdn-icons-png.flaticon.com/512/3448/3448339.png',
  iconSize: [35, 35],
  iconAnchor: [17, 35],
  popupAnchor: [0, -35],
});

// Fetch route from OSRM (including steps)
const fetchRoute = async (startLat, startLng, endLat, endLng) => {
  try {
    const response = await fetch(
      `https://router.project-osrm.org/route/v1/driving/${startLng},${startLat};${endLng},${endLat}?overview=full&geometries=geojson&steps=true`
    );
    const data = await response.json();
    if (data.code === 'Ok' && data.routes?.[0]) {
      const route = data.routes[0];
      const coordinates = route.geometry.coordinates.map(([lng, lat]) => [lat, lng]);
      const distance = (route.distance / 1000).toFixed(2);
      const duration = Math.round(route.duration / 60);
      const steps = route.legs?.[0]?.steps?.map(step => ({
        instruction: step.maneuver?.instruction || step.name || '',
        distance: (step.distance / 1000).toFixed(1),
      })) || [];
      return { coordinates, distance, duration, steps };
    }
    return null;
  } catch (err) {
    console.error('Route fetch error:', err);
    return null;
  }
};

// Fallback straight-line distance (Haversine)
const haversineDistance = (lat1, lng1, lat2, lng2) => {
  const R = 6371;
  const dLat = (lat2 - lat1) * Math.PI / 180;
  const dLng = (lng2 - lng1) * Math.PI / 180;
  const a =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) *
    Math.sin(dLng / 2) ** 2;
  return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
};

// Component to set initial bounds only once (receives map instance as prop)
const SetInitialBounds = ({ positions, map }) => {
  const hasSet = useRef(false);
  useEffect(() => {
    if (!hasSet.current && map && positions && positions.length > 1) {
      const bounds = L.latLngBounds(positions);
      map.fitBounds(bounds, { padding: [60, 60] });
      hasSet.current = true;
    }
  }, [map, positions]);
  return null;
};

const TrackingMap = ({
  pickupLat, pickupLng,
  dropoffLat, dropoffLng,
  driverLat, driverLng,
  driverName,
  status = 'assigned',
  isLiveGPS = false,
  onRouteUpdate,
  followDriver = false,
  onMapReady,
}) => {
  const [fullTripRoute, setFullTripRoute] = useState(null);
  const [activeRoute, setActiveRoute] = useState(null);
  const [loadingRoute, setLoadingRoute] = useState(true);
  const lastDriverPos = useRef({ lat: null, lng: null });
  const updateTimer = useRef(null);
  const mapRef = useRef(null); // store Leaflet map instance

  const isAssigned = status === 'assigned';
  const isPickedUp = status === 'picked_up';
  const isDelivered = status === 'delivered';

  // Target: pickup when assigned, dropoff when picked up
  const targetLat = isAssigned ? pickupLat : (isPickedUp ? dropoffLat : null);
  const targetLng = isAssigned ? pickupLng : (isPickedUp ? dropoffLng : null);

  // Full trip route (pickup → dropoff) – once, reference
  useEffect(() => {
    if (!pickupLat || !pickupLng || !dropoffLat || !dropoffLng) return;
    setLoadingRoute(true);
    fetchRoute(pickupLat, pickupLng, dropoffLat, dropoffLng).then(route => {
      if (route) setFullTripRoute(route);
      setLoadingRoute(false);
    });
  }, [pickupLat, pickupLng, dropoffLat, dropoffLng]);

  // Real-time route: driver → target
  const updateActiveRoute = useCallback(async () => {
    if (!driverLat || !driverLng || !targetLat || !targetLng) return;

    // Throttle: only update if driver moved > 50m
    const last = lastDriverPos.current;
    let distanceMoved = 0;
    if (last.lat && last.lng) {
      distanceMoved = haversineDistance(last.lat, last.lng, driverLat, driverLng);
    }
    if (distanceMoved < 0.05 && last.lat) return; // less than 50 meters, skip
    lastDriverPos.current = { lat: driverLat, lng: driverLng };

    const route = await fetchRoute(driverLat, driverLng, targetLat, targetLng);
    if (route) {
      setActiveRoute(route);
      if (onRouteUpdate) onRouteUpdate({ dist: route.distance, time: route.duration, steps: route.steps });
    } else {
      // fallback straight line
      const dist = haversineDistance(driverLat, driverLng, targetLat, targetLng);
      setActiveRoute({
        coordinates: [[driverLat, driverLng], [targetLat, targetLng]],
        distance: dist.toFixed(2),
        duration: Math.round((dist / 60) * 60),
        steps: [],
      });
      if (onRouteUpdate) onRouteUpdate({ dist: dist.toFixed(2), time: Math.round((dist / 60) * 60), steps: [] });
    }
  }, [driverLat, driverLng, targetLat, targetLng, onRouteUpdate]);

  // Trigger route update when driver moves or target changes
  useEffect(() => {
    if (!driverLat || !driverLng || !targetLat || !targetLng) return;
    updateActiveRoute(); // immediate update
    updateTimer.current = setInterval(updateActiveRoute, 5000); // also every 5 seconds
    return () => {
      if (updateTimer.current) clearInterval(updateTimer.current);
    };
  }, [driverLat, driverLng, targetLat, targetLng, updateActiveRoute]);

  // Follow driver mode: centre map when followDriver true and driver coordinates change
  useEffect(() => {
    if (followDriver && driverLat && driverLng && mapRef.current) {
      mapRef.current.flyTo([driverLat, driverLng], Math.max(mapRef.current.getZoom(), 14), { duration: 0.5 });
    }
  }, [driverLat, driverLng, followDriver]);

  const center = pickupLat && dropoffLat
    ? [(pickupLat + dropoffLat) / 2, (pickupLng + dropoffLng) / 2]
    : [-26.2041, 28.0473];

  const boundPositions = [
    pickupLat && pickupLng ? [pickupLat, pickupLng] : null,
    dropoffLat && dropoffLng ? [dropoffLat, dropoffLng] : null,
    driverLat && driverLng ? [driverLat, driverLng] : null,
  ].filter(Boolean);

  const remainingDist = activeRoute?.distance;
  const remainingEta = activeRoute?.duration;

  // Colour: purple for pickup phase, green for dropoff phase
  const routeColor = isPickedUp ? '#10b981' : '#8b5cf6';
  const borderColor = isPickedUp ? '#10b981' : '#8b5cf6';

  return (
    <div style={{ position: 'relative', height: '100%', minHeight: '400px' }}>
      <MapContainer
        center={center}
        zoom={12}
        rotate={true}
        zoomControl={true}
        touchZoom={true}
        style={{ height: '100%', width: '100%', minHeight: '400px', borderRadius: '8px' }}
        ref={(mapInstance) => {
          if (mapInstance) {
            mapRef.current = mapInstance;
            if (onMapReady) onMapReady(mapInstance);
          }
        }}
      >
        <TileLayer
          url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
          attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
        />

        <SetInitialBounds positions={boundPositions} map={mapRef.current} />

        {/* Markers */}
        {pickupLat && pickupLng && (
          <Marker position={[pickupLat, pickupLng]} icon={pickupIcon}>
            <Popup><strong>📦 Pickup Location</strong></Popup>
          </Marker>
        )}
        {dropoffLat && dropoffLng && (
          <Marker position={[dropoffLat, dropoffLng]} icon={dropoffIcon}>
            <Popup>
              <strong>🏁 Drop-off Location</strong>
              {fullTripRoute && (
                <div style={{ marginTop: '6px', fontSize: '12px' }}>
                  Total Distance: {fullTripRoute.distance} km<br />
                  Estimated Time: ~{fullTripRoute.duration} min
                </div>
              )}
            </Popup>
          </Marker>
        )}
        {driverLat && driverLng && (
          <Marker position={[driverLat, driverLng]} icon={driverIcon}>
            <Popup>
              <strong>🚛 {driverName || 'Driver'}</strong>
              {isLiveGPS && <div style={{ fontSize: '11px', color: '#10b981', marginTop: '4px' }}>● Live GPS</div>}
              {remainingDist && (
                <div style={{ marginTop: '6px', fontSize: '12px' }}>
                  {isPickedUp ? 'Distance to drop-off' : 'Distance to pickup'}: {remainingDist} km<br />
                  ETA: ~{remainingEta} min
                </div>
              )}
            </Popup>
          </Marker>
        )}

        {/* Active route (driver → target) – updates live */}
        {activeRoute?.coordinates && (
          <Polyline
            positions={activeRoute.coordinates}
            color={routeColor}
            weight={5}
            opacity={0.9}
          />
        )}

        {/* Full trip reference (pickup → dropoff) – light blue dashed */}
        {fullTripRoute?.coordinates && (
          <Polyline
            positions={fullTripRoute.coordinates}
            color="#3b82f6"
            weight={5}
            opacity={0.8}
            dashArray="15, 10"
          />
        )}
      </MapContainer>

      {/* Info Panel */}
      {(remainingDist || fullTripRoute) && (
        <div style={{
          position: 'absolute',
          bottom: '20px',
          right: '20px',
          backgroundColor: '#1f2937',
          padding: '12px 20px',
          borderRadius: '8px',
          borderLeft: `4px solid ${borderColor}`,
          zIndex: 1000,
          boxShadow: '0 4px 6px rgba(0,0,0,0.3)',
          minWidth: '200px',
        }}>
          {fullTripRoute && (
            <>
              <div style={{ fontSize: '11px', color: '#9ca3af' }}>Total Trip (Reference)</div>
              <div style={{ fontSize: '18px', fontWeight: 'bold', color: '#eab308' }}>
                {fullTripRoute.distance} km
              </div>
              <div style={{ fontSize: '11px', color: '#9ca3af' }}>Est. Total Time</div>
              <div style={{ fontSize: '14px', fontWeight: 'bold', color: '#60a5fa' }}>
                ~{fullTripRoute.duration} min
              </div>
              <div style={{ height: '1px', backgroundColor: '#374151', margin: '8px 0' }} />
            </>
          )}
          {remainingDist && (
            <>
              <div style={{ fontSize: '11px', color: '#9ca3af' }}>
                {isPickedUp ? '🟢 To Drop-off' : '🟣 To Pickup'}
              </div>
              <div style={{ fontSize: '20px', fontWeight: 'bold', color: borderColor }}>
                {remainingDist} km
              </div>
              <div style={{ fontSize: '11px', color: '#9ca3af' }}>ETA</div>
              <div style={{ fontSize: '16px', fontWeight: 'bold', color: '#facc15' }}>
                ~{remainingEta} min
              </div>
            </>
          )}
          {loadingRoute && <div style={{ fontSize: '12px', color: '#9ca3af', textAlign: 'center', marginTop: '8px' }}>⏳ Loading route...</div>}
          <div style={{
            marginTop: '10px',
            padding: '6px 10px',
            borderRadius: '6px',
            backgroundColor: isDelivered ? '#064e3b' : isPickedUp ? '#1a3a2a' : '#1a2540',
            fontSize: '11px',
            fontWeight: 'bold',
            color: isDelivered ? '#34d399' : isPickedUp ? '#10b981' : '#a78bfa',
            textAlign: 'center',
          }}>
            {isDelivered ? '✅ Delivered' : isPickedUp ? '🚛 En Route to Drop-off' : '📍 Heading to Pickup'}
          </div>
        </div>
      )}
    </div>
  );
};

export default TrackingMap;
